import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { API, axios } from "@/api";
import { useCopyBank } from "@/hooks/useCopyBank";
import { ThumbsUp, Sparkles, MapPin, Tag, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CommunityPicks() {
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [votingId, setVotingId] = useState(null);
  const { getText } = useCopyBank();

  const fetchPicks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/submissions/pending`);
      setPicks(res.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPicks(); }, [fetchPicks]);

  const handleUpvote = async (subId) => {
    if (votingId) return;
    setVotingId(subId);
    try {
      const res = await axios.post(`${API}/submissions/${subId}/upvote`);
      const { new_count, auto_approved } = res.data;
      if (auto_approved) {
        setPicks(prev => prev.filter(p => p.id !== subId));
        toast.success("Community approved! This deal is now live.");
      } else {
        setPicks(prev => prev.map(p =>
          p.id === subId ? { ...p, upvotes: new_count, user_voted: true } : p
        ));
        toast.success("Vote recorded!");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not vote";
      toast.error(msg);
    } finally { setVotingId(null); }
  };

  if (loading || picks.length === 0) return null;

  return (
    <div className="px-4 mb-5" data-testid="community-picks-section">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-amber-500" />
        <h3 className="text-sm font-bold text-slate-800" data-testid="community-picks-header">
          {getText("submit_business")}
        </h3>
        <span className="ml-auto text-[10px] font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
          {picks.length} pending
        </span>
      </div>
      <div className="space-y-2.5">
        <AnimatePresence mode="popLayout">
          {picks.slice(0, 5).map((pick) => (
            <motion.div
              key={pick.id}
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -60, scale: 0.95 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-xl border border-slate-100 shadow-sm p-3.5 flex gap-3"
              data-testid={`community-pick-${pick.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate" data-testid={`pick-name-${pick.id}`}>
                  {pick.business_name}
                </p>
                <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-400">
                  {pick.category && (
                    <span className="flex items-center gap-0.5">
                      <Tag className="w-3 h-3" />{pick.category}
                    </span>
                  )}
                  {pick.location && (
                    <span className="flex items-center gap-0.5">
                      <MapPin className="w-3 h-3" />{pick.location}
                    </span>
                  )}
                </div>
                {pick.offer_description && (
                  <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{pick.offer_description}</p>
                )}
                <p className="text-[10px] text-slate-400 mt-1">by {pick.user_name}</p>
              </div>
              <div className="flex flex-col items-center justify-center gap-1 min-w-[56px]">
                <button
                  onClick={() => handleUpvote(pick.id)}
                  disabled={pick.user_voted || votingId === pick.id}
                  className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
                    pick.user_voted
                      ? "bg-amber-100 text-amber-600 cursor-default"
                      : "bg-slate-50 text-slate-400 hover:bg-amber-50 hover:text-amber-500 active:scale-95"
                  }`}
                  data-testid={`upvote-btn-${pick.id}`}
                >
                  {votingId === pick.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ThumbsUp className={`w-4.5 h-4.5 ${pick.user_voted ? "fill-amber-500" : ""}`} />
                  )}
                </button>
                <span className={`text-xs font-bold ${pick.user_voted ? "text-amber-600" : "text-slate-500"}`}
                      data-testid={`upvote-count-${pick.id}`}>
                  {pick.upvotes}
                </span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
